/**
 * Shared overlay primitives for both the device (Android) and desktop
 * (VNC) viewer branches. Originally lived inline inside DesktopView and
 * the App-level error block; extracted so a single visual language
 * covers every viewer state (loading / kicked / connection-lost /
 * fatal) and stays in sync between the two branches.
 *
 * Sized and tokenised to render correctly both at full screen
 * (standalone "open in new tab") and inside the small ~270x480 phone
 * iframe embedded in PhoneSidebar — see `--device-overlay-*` CSS vars
 * in globals.css.
 */
import type { CSSProperties, ReactNode } from "react";

type Tone = "info" | "warning" | "error";

export interface ViewerOverlayProps {
  /**
   * Visual icon slot. Pass a 28x28 SVG; tone styles the surrounding
   * container. Use `<ViewerSpinner />` (separate component) for the
   * loading state — it has its own layout and ignores `tone`.
   */
  icon: ReactNode;
  tone: Tone;
  title: string;
  description?: string;
  /**
   * Optional primary action (most often the "reconnect" button). When
   * omitted the card is purely informational (e.g. fatal auth errors
   * the viewer can't recover from).
   */
  action?: ReactNode;
  /**
   * Compact mode for tiny iframe embeds (PhoneSidebar's 9:16 panel is
   * ~270x480). Trims padding and font sizes via the `--device-*`
   * variants of the layout tokens.
   */
  compact?: boolean;
}

export function ViewerOverlay({
  icon,
  tone,
  title,
  description,
  action,
  compact,
}: ViewerOverlayProps) {
  const cardStyle: CSSProperties = compact
    ? {
        gap: "var(--device-overlay-stack-gap)",
        paddingLeft: "var(--device-overlay-pad-x)",
        paddingRight: "var(--device-overlay-pad-x)",
        paddingTop: "var(--device-overlay-pad-y)",
        paddingBottom: "var(--device-overlay-pad-y)",
        borderRadius: "var(--device-overlay-radius)",
      }
    : {
        gap: "var(--desktop-overlay-stack-gap)",
        paddingLeft: "var(--desktop-overlay-pad-x)",
        paddingRight: "var(--desktop-overlay-pad-x)",
        paddingTop: "var(--desktop-overlay-pad-y)",
        paddingBottom: "var(--desktop-overlay-pad-y)",
        borderRadius: "var(--desktop-overlay-radius)",
      };

  return (
    <div
      className="flex w-full flex-col items-center text-center"
      style={{
        ...cardStyle,
        background:
          "linear-gradient(165deg, rgba(34,34,38,0.55) 0%, rgba(16,16,18,0.72) 100%)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow:
          "0 0 0 1px rgba(0,0,0,0.35) inset, 0 1px 0 0 rgba(255,255,255,0.06) inset, 0 32px 64px -24px rgba(0,0,0,0.85)",
      }}
    >
      <ViewerStateIcon tone={tone} compact={compact}>
        {icon}
      </ViewerStateIcon>
      <div
        className="flex w-full max-w-md flex-col items-center text-center"
        style={{ gap: "var(--desktop-overlay-text-gap)" }}
      >
        <h2
          className={
            compact
              ? "text-sm font-semibold tracking-[-0.01em] text-white"
              : "text-base font-semibold tracking-[-0.02em] text-white"
          }
        >
          {title}
        </h2>
        {description && (
          <p
            className={
              compact
                ? "text-[12px] leading-[1.5] antialiased"
                : "text-[13px] leading-[1.55] antialiased"
            }
            style={{ color: "var(--color-text-muted)" }}
          >
            {description}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * Tonal icon container. Reuses the `--desktop-overlay-radius-inner`
 * token so the icon slot, the primary button, and any future surface
 * stay visually grouped.
 */
export function ViewerStateIcon({
  tone,
  compact,
  children,
}: {
  tone: Tone;
  compact?: boolean;
  children: ReactNode;
}) {
  const palette =
    tone === "error"
      ? {
          border: "rgba(255, 107, 107, 0.22)",
          bg: "rgba(255, 107, 107, 0.08)",
        }
      : tone === "warning"
        ? {
            border: "rgba(251, 191, 36, 0.28)",
            bg: "rgba(251, 191, 36, 0.09)",
          }
        : {
            border: "rgba(96, 165, 250, 0.28)",
            bg: "rgba(96, 165, 250, 0.09)",
          };
  const size = compact ? "h-[3.5rem] w-[3.5rem]" : "h-[4.75rem] w-[4.75rem]";
  const iconSize = compact ? "[&>svg]:h-[1.35rem] [&>svg]:w-[1.35rem]" : "[&>svg]:h-[1.65rem] [&>svg]:w-[1.65rem]";
  return (
    <div
      className={`flex shrink-0 items-center justify-center ${size}`}
      style={{
        borderRadius: "var(--desktop-overlay-radius-inner)",
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        boxShadow: "0 0 24px -8px rgba(0,0,0,0.5)",
      }}
      aria-hidden
    >
      <div className={`flex items-center justify-center ${iconSize}`}>{children}</div>
    </div>
  );
}

/**
 * The primary action button used inside `<ViewerOverlay action=... />`.
 * Pull-quote-blue gradient matches the desktop branch's existing
 * "Retry" CTA; consumers should pass an icon (`<RefreshCwIcon />`) plus
 * label text as children.
 */
export function ViewerPrimaryButton({
  children,
  onClick,
  compact,
}: {
  children: ReactNode;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        compact
          ? "box-border inline-flex min-h-[36px] w-full cursor-pointer items-center justify-center gap-2 px-4 text-[12px] font-semibold tracking-[-0.01em] text-white shadow-lg transition-[filter,transform,box-shadow] duration-200 hover:brightness-110 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(12,12,14,0.95)]"
          : "box-border inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2.5 px-6 text-[13px] font-semibold tracking-[-0.01em] text-white shadow-lg transition-[filter,transform,box-shadow] duration-200 hover:brightness-110 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(12,12,14,0.95)]"
      }
      style={{
        borderRadius: "var(--desktop-overlay-radius-inner)",
        paddingTop: compact ? "0.5rem" : "0.6875rem",
        paddingBottom: compact ? "0.5rem" : "0.6875rem",
        background:
          "linear-gradient(180deg, var(--color-accent-hover) 0%, var(--color-accent) 100%)",
        boxShadow:
          "0 1px 0 0 rgba(255,255,255,0.16) inset, 0 10px 28px -10px rgba(59, 130, 246, 0.6)",
      }}
    >
      {children}
    </button>
  );
}

/**
 * The "connecting" spinner used in the loading overlay. Wrapped in
 * a square slot identical to `<ViewerStateIcon>` so loading and error
 * cards share the same vertical rhythm.
 */
export function ViewerSpinner({ compact }: { compact?: boolean }) {
  const r = compact ? 18 : 22;
  const c = 2 * Math.PI * r;
  const dash = c * 0.22;
  const gap = c - dash;
  const wrapperSize = compact ? "h-[3.5rem] w-[3.5rem]" : "h-[4.75rem] w-[4.75rem]";
  const svgSize = compact ? "h-[2.5rem] w-[2.5rem]" : "h-[3.25rem] w-[3.25rem]";
  return (
    <div
      className={`relative flex shrink-0 items-center justify-center ${wrapperSize}`}
      aria-hidden
    >
      <div
        className="pointer-events-none absolute inset-[-8px] animate-pulse rounded-full opacity-80"
        style={{
          background:
            "radial-gradient(circle, color-mix(in srgb, var(--color-accent) 42%, transparent) 0%, transparent 68%)",
        }}
      />
      <svg
        className={`relative ${svgSize} text-[var(--color-accent)]`}
        viewBox="0 0 56 56"
        fill="none"
        aria-hidden
      >
        <circle cx="28" cy="28" r={r} stroke="rgba(255,255,255,0.09)" strokeWidth="3" />
        <g
          className="desktop-overlay-spin"
          style={{ transformOrigin: "28px 28px" }}
        >
          <circle
            cx="28"
            cy="28"
            r={r}
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${gap}`}
            transform="rotate(-90 28 28)"
          />
        </g>
      </svg>
    </div>
  );
}

/* --- Shared icon set --------------------------------------------------- */

export function RefreshCwIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </svg>
  );
}

export function WifiOffIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="2" x2="22" y1="2" y2="22" />
      <path d="M8.5 8.5c-.7.4-1.4.9-2 1.5" />
      <path d="M6.3 6.3C8 5 10.2 4 12.5 4c2.5 0 4.8 1 6.5 2.7" />
      <path d="M18.1 10.1c.6.7 1.1 1.5 1.5 2.4" />
      <path d="M12 19h.01" />
      <path d="M9.1 15.1a4 4 0 0 1 5.8 0" />
    </svg>
  );
}

export function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

/**
 * Two-overlapping-rectangles glyph for "another viewer connected".
 * The smaller rect on the upper-right hints at the second viewer
 * superseding the original session — chosen over a plain `users` icon
 * because it reads better at the tiny embed size.
 */
export function ScreenShareTakeoverIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="5" width="14" height="10" rx="1.5" />
      <path d="M7 19h6" />
      <path d="M10 15v4" />
      <rect x="14" y="9" width="8" height="6" rx="1.2" fill="currentColor" fillOpacity="0.18" />
    </svg>
  );
}
